/**
 * DashboardChartsView - Vue graphiques détaillée du dashboard
 * Style QuickBooks/Sage - Professionnel et élégant
 */
import { useState, useMemo } from 'react';
import { 
  BarChart3, 
  PieChart as PieChartIcon, 
  TrendingUp, 
  Wallet,
  Building2,
  Calendar,
  Filter,
  RefreshCw,
  Download
} from 'lucide-react';
import { TreasuryChart, TreasuryDataPoint } from '../charts/TreasuryChart';
import { RevenueExpenseChart } from '../charts/RevenueChart';
import { ExpensesPieChart } from '../charts/ExpensesPieChart';
import { useBusinessUnitStore } from '../../stores/businessUnitStore';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { useCurrency } from '../../hooks/useCurrency';

// Types
export interface ChartsPanelData {
  // Time series data
  revenueExpenses: Array<{ date: string; revenue: number; expenses: number }>;
  treasury: TreasuryDataPoint[];
  // Breakdown data
  expenseBreakdown: Array<{ name: string; value: number; color: string; percentage?: number }>;
  // KPI Summary
  summary: {
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    profitMargin: number;
    totalCash: number;
    cashChange: number;
  };
}

type ChartPeriod = 'week' | 'month' | 'quarter' | 'year';
type ActiveChart = 'all' | 'treasury' | 'revenue' | 'expenses';

interface DashboardChartsViewProps {
  data: ChartsPanelData;
  isLoading?: boolean;
  onRefresh?: () => void;
  onPeriodChange?: (period: ChartPeriod) => void;
  className?: string;
}

const PERIOD_OPTIONS: { value: ChartPeriod; label: string }[] = [
  { value: 'week', label: '7 jours' },
  { value: 'month', label: '30 jours' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Année' }
];

const CHART_TABS: { id: ActiveChart; label: string; icon: React.ElementType }[] = [
  { id: 'all', label: 'Vue d\'ensemble', icon: BarChart3 },
  { id: 'treasury', label: 'Trésorerie', icon: Wallet },
  { id: 'revenue', label: 'Revenus & Charges', icon: TrendingUp },
  { id: 'expenses', label: 'Répartition', icon: PieChartIcon }
];

export function DashboardChartsView({
  data,
  isLoading = false,
  onRefresh,
  onPeriodChange,
  className = ''
}: DashboardChartsViewProps) {
  const [activeChart, setActiveChart] = useState<ActiveChart>('all');
  const [period, setPeriod] = useState<ChartPeriod>('month');
  const [showFilters, setShowFilters] = useState(false);

  const { selectedBusinessUnit } = useBusinessUnitStore();
  const { currentFiscalYear } = useFiscalYearStore();
  const { format: formatCurrency } = useCurrency();

  // Libellé du contexte business unit
  const businessUnitLabel = useMemo(() => {
    if (!selectedBusinessUnit) return 'Toute l\'entreprise';
    if (selectedBusinessUnit.type === 'company') return selectedBusinessUnit.name;
    if (selectedBusinessUnit.type === 'branch') return `Agence: ${selectedBusinessUnit.name}`;
    if (selectedBusinessUnit.type === 'pos') return `PDV: ${selectedBusinessUnit.name}`;
    return selectedBusinessUnit.name;
  }, [selectedBusinessUnit]);

  const handlePeriodChange = (newPeriod: ChartPeriod) => {
    setPeriod(newPeriod);
    onPeriodChange?.(newPeriod);
  };

  // KPI Cards compactes - Responsive
  const KPISummaryCards = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 lg:gap-4">
      <SummaryCard 
        label="Chiffre d'affaires"
        value={formatCurrency(data.summary.totalRevenue)}
        trend={data.summary.totalRevenue > 0 ? 'up' : 'neutral'}
        color="text-green-600"
      />
      <SummaryCard 
        label="Charges"
        value={formatCurrency(data.summary.totalExpenses)}
        trend="neutral"
        color="text-red-600"
      />
      <SummaryCard 
        label="Résultat net"
        value={formatCurrency(data.summary.netProfit)}
        trend={data.summary.netProfit >= 0 ? 'up' : 'down'}
        color={data.summary.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}
      />
      <SummaryCard 
        label="Marge nette"
        value={`${data.summary.profitMargin.toFixed(1)}%`}
        trend={data.summary.profitMargin >= 10 ? 'up' : data.summary.profitMargin >= 0 ? 'neutral' : 'down'}
        color={data.summary.profitMargin >= 0 ? 'text-blue-600' : 'text-red-600'}
      />
      <SummaryCard 
        label="Trésorerie"
        value={formatCurrency(data.summary.totalCash)}
        trend={data.summary.cashChange >= 0 ? 'up' : 'down'}
        color="text-purple-600"
      />
      <SummaryCard 
        label="Variation tréso"
        value={formatCurrency(data.summary.cashChange)}
        trend={data.summary.cashChange >= 0 ? 'up' : 'down'}
        color={data.summary.cashChange >= 0 ? 'text-green-600' : 'text-red-600'}
        showSign
      />
    </div>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header avec contexte et filtres */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-card text-foreground rounded-2xl border border-border shadow-sm p-4">
        {/* Contexte */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-4 w-4" />
            </span>
            <span className="font-semibold text-foreground">
              {businessUnitLabel}
            </span>
          </div>
          {currentFiscalYear && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 px-2 py-1 rounded-full">
              <Calendar className="h-3.5 w-3.5" />
              <span>{currentFiscalYear.code}</span>
            </div>
          )}
        </div>

        {/* Actions et filtres */}
        <div className="flex items-center gap-2">
          {/* Sélecteur de période */}
          <div className="flex items-center bg-muted/70 rounded-xl p-1 shadow-inner">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handlePeriodChange(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  period === opt.value
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Bouton filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-xl border border-border transition-all ${
              showFilters 
                ? 'bg-primary text-primary-foreground shadow-sm' 
                : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60'
            }`}
          >
            <Filter className="h-4 w-4" />
          </button>

          {/* Refresh */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-2 rounded-xl border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* KPI Summary */}
      <KPISummaryCards />

      {/* Navigation des graphiques */}
      <div className="flex items-center gap-1 bg-card rounded-2xl border border-border shadow-sm p-1.5">
        {CHART_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveChart(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              activeChart === tab.id
                ? 'bg-primary/10 text-primary border border-primary/20'
                : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Zone des graphiques */}
      <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-[400px]">
            <div className="flex flex-col items-center gap-3">
              <RefreshCw className="h-8 w-8 text-primary animate-spin" />
              <span className="text-sm text-gray-500">Chargement des données...</span>
            </div>
          </div>
        )}

        {/* Content */}
        {!isLoading && (
          <>
            {/* Vue d'ensemble - Grid avec tous les graphiques */}
            {activeChart === 'all' && (
              <div className="space-y-8">
                {/* Row 1: Treasury + Revenue */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-border bg-muted/20 p-4 shadow-sm">
                    <TreasuryChart 
                      data={data.treasury}
                      period={period === 'year' ? 'month' : period === 'quarter' ? 'week' : 'day'}
                      showInflows={true}
                      showOutflows={true}
                      showNetPosition={true}
                    />
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/20 p-4 shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Revenus vs Charges
                    </h3>
                    <RevenueExpenseChart 
                      data={data.revenueExpenses}
                      showRevenue={true}
                      showExpenses={true}
                    />
                  </div>
                </div>

                {/* Row 2: Pie chart - Full width mais compact */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 rounded-2xl border border-border bg-muted/20 p-4 shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Tendances mensuelles
                    </h3>
                    <RevenueExpenseChart 
                      data={data.revenueExpenses}
                      showRevenue={true}
                      showExpenses={true}
                    />
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/20 p-4 shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Répartition des charges
                    </h3>
                    <ExpensesPieChart data={data.expenseBreakdown} />
                  </div>
                </div>
              </div>
            )}

            {/* Vue Trésorerie détaillée */}
            {activeChart === 'treasury' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">
                      Analyse de la trésorerie
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Suivi des flux de trésorerie et position nette
                    </p>
                  </div>
                  <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/60 transition-colors">
                    <Download className="h-4 w-4" />
                    Exporter
                  </button>
                </div>
                <TreasuryChart 
                  data={data.treasury}
                  period={period === 'year' ? 'month' : period === 'quarter' ? 'week' : 'day'}
                  showInflows={true}
                  showOutflows={true}
                  showNetPosition={true}
                  className="h-[400px]"
                />
                
                {/* Métriques détaillées */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border/60">
                  <MetricDetail 
                    label="Position actuelle"
                    value={formatCurrency(data.summary.totalCash)}
                    subtext="Solde total"
                  />
                  <MetricDetail 
                    label="Variation période"
                    value={formatCurrency(data.summary.cashChange)}
                    subtext={data.summary.cashChange >= 0 ? 'En hausse' : 'En baisse'}
                    isPositive={data.summary.cashChange >= 0}
                  />
                  <MetricDetail 
                    label="Moyenne entrées"
                    value={formatCurrency(calculateAverage(data.treasury, 'inflow'))}
                    subtext="Par période"
                  />
                  <MetricDetail 
                    label="Moyenne sorties"
                    value={formatCurrency(calculateAverage(data.treasury, 'outflow'))}
                    subtext="Par période"
                  />
                </div>
              </div>
            )}

            {/* Vue Revenus & Charges */}
            {activeChart === 'revenue' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">
                      Évolution des revenus et charges
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Analyse comparative sur la période sélectionnée
                    </p>
                  </div>
                  <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/60 transition-colors">
                    <Download className="h-4 w-4" />
                    Exporter
                  </button>
                </div>
                <div className="h-[400px]">
                  <RevenueExpenseChart 
                    data={data.revenueExpenses}
                    showRevenue={true}
                    showExpenses={true}
                  />
                </div>
                
                {/* Métriques détaillées */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border/60">
                  <MetricDetail 
                    label="Total revenus"
                    value={formatCurrency(data.summary.totalRevenue)}
                    subtext="Sur la période"
                  />
                  <MetricDetail 
                    label="Total charges"
                    value={formatCurrency(data.summary.totalExpenses)}
                    subtext="Sur la période"
                  />
                  <MetricDetail 
                    label="Résultat"
                    value={formatCurrency(data.summary.netProfit)}
                    subtext={data.summary.netProfit >= 0 ? 'Bénéfice' : 'Perte'}
                    isPositive={data.summary.netProfit >= 0}
                  />
                  <MetricDetail 
                    label="Marge nette"
                    value={`${data.summary.profitMargin.toFixed(1)}%`}
                    subtext="Rentabilité"
                    isPositive={data.summary.profitMargin >= 0}
                  />
                </div>
              </div>
            )}

            {/* Vue Répartition des charges */}
            {activeChart === 'expenses' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground">
                      Répartition des charges
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Analyse par catégorie de dépenses
                    </p>
                  </div>
                  <button className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/60 transition-colors">
                    <Download className="h-4 w-4" />
                    Exporter
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="h-[400px]">
                    <ExpensesPieChart data={data.expenseBreakdown} />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground mb-4">
                      Détail par catégorie
                    </h4>
                    {data.expenseBreakdown.map((item, idx) => (
                      <ExpenseRow 
                        key={idx}
                        name={item.name}
                        value={formatCurrency(item.value)}
                        percentage={item.percentage || 0}
                        color={item.color}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Composants auxiliaires - Responsive
function SummaryCard({ 
  label, 
  value, 
  trend, 
  color,
  showSign = false 
}: { 
  label: string; 
  value: string; 
  trend: 'up' | 'down' | 'neutral'; 
  color: string;
  showSign?: boolean;
}) {
  return (
    <div className="bg-card rounded-xl sm:rounded-2xl border border-border p-2.5 sm:p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow min-w-0">
      <div className="flex items-start justify-between gap-1">
        <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide truncate flex-1">{label}</p>
        <span className={`h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full flex-shrink-0 ${
          trend === 'up' ? 'bg-green-500' : trend === 'down' ? 'bg-red-500' : 'bg-muted-foreground'
        }`} />
      </div>
      <p className={`mt-1.5 sm:mt-2 text-sm sm:text-base lg:text-lg font-semibold ${color} truncate`} title={value}>
        {showSign && parseFloat(value.replace(/[^\d.-]/g, '')) > 0 ? '+' : ''}{value}
      </p>
      <div className="mt-2 sm:mt-3 h-1 sm:h-1.5 w-full rounded-full bg-muted/60 overflow-hidden">
        <div 
          className={`h-1 sm:h-1.5 rounded-full transition-all ${
            trend === 'up' ? 'bg-green-500' : trend === 'down' ? 'bg-red-500' : 'bg-muted-foreground'
          }`}
          style={{ width: trend === 'up' ? '80%' : trend === 'down' ? '30%' : '55%' }}
        />
      </div>
    </div>
  );
}

function MetricDetail({ 
  label, 
  value, 
  subtext,
  isPositive 
}: { 
  label: string; 
  value: string; 
  subtext: string;
  isPositive?: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-card/60 px-4 py-3 text-center shadow-sm">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-semibold mt-1 ${
        isPositive !== undefined 
          ? (isPositive ? 'text-green-600' : 'text-red-600')
          : 'text-foreground'
      }`}>
        {value}
      </p>
      <p className="text-[11px] text-muted-foreground mt-1">{subtext}</p>
    </div>
  );
}

function ExpenseRow({ 
  name, 
  value, 
  percentage, 
  color 
}: { 
  name: string; 
  value: string; 
  percentage: number; 
  color: string;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-card/60 border border-border rounded-xl shadow-sm">
      <div 
        className="w-3 h-3 rounded-full flex-shrink-0" 
        style={{ backgroundColor: color }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground truncate">
            {name}
          </span>
          <span className="text-sm font-semibold text-foreground ml-2">
            {value}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-muted/60 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all"
              style={{ width: `${percentage}%`, backgroundColor: color }}
            />
          </div>
          <span className="text-xs text-muted-foreground w-10 text-right">
            {percentage.toFixed(0)}%
          </span>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function calculateAverage(data: TreasuryDataPoint[], key: keyof TreasuryDataPoint): number {
  if (!data.length) return 0;
  const values = data.map(d => Number(d[key]) || 0);
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}
